import { Observable } from "rxjs";
import { useMemo } from "react";
import { distinctUntilKeyChanged, filter, map } from "rxjs/operators";
import { Group } from "@iventis/types";
import { SectionedGroup } from "./types";
import { ContextPropsData } from "./calendar-context";
import { usePropsStream } from "./hooks";

// returns a tree structure of SectionedGroup instances and calculates proportionally the width of each section
// a leaf node will have sectionStart = N and sectionEnd = N+1, whereas a parent of such node which has another 3
// children will could have sectionStart = N and sectionEnd = N + 3
// Following this logic the root of the tree would have sectionStart = 0 and sectionEnd = K,
// where K is the total amount of leaf nodes
const getSectionedTree = (group: Group, prevEnd = 0): SectionedGroup => {
    const subGroups: SectionedGroup[] = group.subGroups
        ? group.subGroups.reduce((acc, group, index) => {
              const end = index === 0 ? prevEnd : acc[index - 1].sectionEnd;
              acc.push(getSectionedTree(group, end));
              return acc;
          }, [])
        : undefined;

    const sectionStart = subGroups?.[0]?.sectionStart || prevEnd;
    const sectionEnd = subGroups?.[subGroups?.length - 1]?.sectionEnd || prevEnd + 1;
    return new SectionedGroup(group, sectionStart, sectionEnd, subGroups);
};

// given a tree structure of SectionedGroup objects, this function will return a list of lists of SectionGroup elements.
// This way we get all the SectionedGroup elements at each level
const sliceSectionedTree = (root: SectionedGroup) => {
    const slices = [];
    const slicer = (subGroups: SectionedGroup[], level = 0) => {
        slices[level] = slices[level] || [];
        subGroups.forEach((group) => {
            slices[level].push(group);
            if (group.subGroups) {
                slicer(group.subGroups, level + 1);
            }
        }, []);
    };
    slicer(root.subGroups);
    return slices;
};

// converts an arbitrary tree-like structure of groups into a tree structure that group axis can render
// when all sectionStart and sectionEnd.
export const useSectionedGroups = (): Observable<SectionedGroup[][]> => {
    const props$ = usePropsStream();
    return useMemo(
        () =>
            props$.pipe(
                filter<ContextPropsData>(Boolean),
                distinctUntilKeyChanged("groups"),
                map(({ groups }) => {
                    const root = getSectionedTree({ groupId: "", groupName: "", subGroups: groups });
                    // at this point root.sectionStart = 0 and root.sectionStart = K where K is the total number of leaf nodes in the tree structure
                    const slices = sliceSectionedTree(root);
                    return slices.map((slice) =>
                        slice.map((sectionedGroup: SectionedGroup) => ({
                            ...sectionedGroup,
                            // with this we know which portion of the total width of the container each sectionGroup occupies
                            // where it starts and where it ends as a fraction of the whole
                            sectionStart: sectionedGroup.sectionStart / root.sectionEnd,
                            sectionEnd: sectionedGroup.sectionEnd / root.sectionEnd,
                        }))
                    );
                })
            ),
        [props$]
    );
};
